import { useEffect } from "react";

interface PageMetaProps {
  title: string;
  description: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
  ogImage?: string;
}

const PageMeta = ({ title, description, canonicalPath, jsonLd, ogImage }: PageMetaProps) => {
  useEffect(() => {
    const fullTitle = `${title} | ESKO Servis Teplice`;
    const baseUrl = "https://eskoservis.cz";
    const imageUrl = ogImage || `${baseUrl}/og-image.jpg`;
    
    // Update document title
    document.title = fullTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }

    // Update Open Graph tags
    const ogTitleMeta = document.querySelector('meta[property="og:title"]');
    const ogDescriptionMeta = document.querySelector('meta[property="og:description"]');
    const ogUrlMeta = document.querySelector('meta[property="og:url"]');
    const ogImageMeta = document.querySelector('meta[property="og:image"]');
    
    if (ogTitleMeta) ogTitleMeta.setAttribute("content", fullTitle);
    if (ogDescriptionMeta) ogDescriptionMeta.setAttribute("content", description);
    if (ogUrlMeta && canonicalPath) ogUrlMeta.setAttribute("content", `${baseUrl}${canonicalPath}`);
    if (ogImageMeta) ogImageMeta.setAttribute("content", imageUrl);

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    
    if (twitterTitle) twitterTitle.setAttribute("content", fullTitle);
    if (twitterDescription) twitterDescription.setAttribute("content", description);
    if (twitterImage) twitterImage.setAttribute("content", imageUrl);

    // Update canonical URL
    if (canonicalPath) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute("href", `${baseUrl}${canonicalPath}`);
    }

    // Add/update hreflang tags for language signals
    if (canonicalPath) {
      const fullUrl = `${baseUrl}${canonicalPath}`;
      
      // Czech hreflang
      let hreflangCs = document.querySelector('link[hreflang="cs"]') as HTMLLinkElement;
      if (!hreflangCs) {
        hreflangCs = document.createElement("link");
        hreflangCs.setAttribute("rel", "alternate");
        hreflangCs.setAttribute("hreflang", "cs");
        document.head.appendChild(hreflangCs);
      }
      hreflangCs.setAttribute("href", fullUrl);
      
      // x-default hreflang
      let hreflangDefault = document.querySelector('link[hreflang="x-default"]') as HTMLLinkElement;
      if (!hreflangDefault) {
        hreflangDefault = document.createElement("link");
        hreflangDefault.setAttribute("rel", "alternate");
        hreflangDefault.setAttribute("hreflang", "x-default");
        document.head.appendChild(hreflangDefault);
      }
      hreflangDefault.setAttribute("href", fullUrl);
    }

    // Add JSON-LD structured data
    if (jsonLd) {
      const existingScript = document.querySelector('script[data-page-jsonld]');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      document.title = "ESKO Servis | Elektroinstalace a elektromontáže Teplice";
      const jsonLdScript = document.querySelector('script[data-page-jsonld]');
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, canonicalPath, jsonLd, ogImage]);

  return null;
};

export default PageMeta;
